import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import { SelectInput, DecimalInput, isNumber } from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

const textInputStyle = {
  height: '32px',
  position: 'relative',
  width: '100%',
}

const TYPE_VALID = [
  { name: 'Device Logic', value: 'O' },
]

const RELAY_VALID = [
  { name: 'Relay 1', value: 1 },
  { name: 'Relay 2', value: 2 },
]

const ACTION_VALID = [
  { name: 'Relay Close (On)', value: 'close', number: 1 },
  { name: 'Relay Open (Off)', value: 'open', number: 0 },
]

const SOURCE_VALID = [
  { name: 'None', value: 'NON', number: 0 },
  { name: 'Relay 1 Feedback', value: 'R1F', number: 1 },
  { name: 'Relay 2 Feedback', value: 'R2F', number: 2 },
  { name: 'Analog Input 1', value: 'AN1', number: 3 },
  { name: 'Accessory Port', value: 'AN2', number: 4 },
  { name: 'Digital Input 1', value: 'DI1', number: 5 },
  { name: 'Digital Input 2', value: 'DI2', number: 6 },
  { name: 'Digital Input 3', value: 'DI3', number: 7 },
  { name: 'Flow Meter', value: 'FLW', number: 8 },
]

const COMPARATOR_VALID = [
  { name: 'Less Than', value: '<' },
  { name: 'Equal To', value: '=' },
  { name: 'Greater Than', value: '>' },
]

const timeMaker = (num, first) => {
  const values = []
  for (let index = 0; index < num; index++) {
    if (!first && index === 0) {
      // nothing to do here
    } else if (first && index === 0) {
      values.push(first)
    } else {
      values.push({ value: index, name: `${index} MIN` })
    }
  }
  return values
}

const DEFAULT_SETTINGS = {
  type: 'O',
  version: '1.0',
  relay: 1,
  action: 'open',
  runTimeMax: 0,
  runTimeMin: 0,
  runTimeCooldown: 0,
  startConditionDelay: 0,
  stopConditionDelay: 0,
  startConditionSource: 'NON',
  stopConditionSource: 'NON',
  startConditionComparator: '<',
  stopConditionComparator: '<',
  startConditionLevel: 0,
  stopConditionLevel: 0,
}

function Pump (props) {
  const defaultSettings = { ...DEFAULT_SETTINGS }

  const {
    device: {
      application_settings: { settings: { logic = defaultSettings } = {} } = {},
      application_settings_new: { settings: { logic: logicNew = defaultSettings } = {} } = {},
    } = {},
    actuator = '1',
    setValidity,
  } = props

  let logicN = {}
  if (logic && logic[actuator]) {
    logicN = logic[actuator]
  }
  let logicNewN = {}
  if (logicNew && logicNew[actuator]) {
    logicNewN = logicNew[actuator]
  }

  const {
    type: defaultType = DEFAULT_SETTINGS.type,
    version: defaultVersion = DEFAULT_SETTINGS.version,
    relay: defaultRelay = actuator,
    action: defaultAction = DEFAULT_SETTINGS.action,
    runTimeMax: defaultRunTimeMax = DEFAULT_SETTINGS.runTimeMax,
    runTimeMin: defaultRunTimeMin = DEFAULT_SETTINGS.runTimeMin,
    runTimeCooldown: defaultRunTimeCooldown = DEFAULT_SETTINGS.runTimeCooldown,
    startConditions: [{
      delay: defaultStartConditionDelay = DEFAULT_SETTINGS.startConditionDelay,
      source: defaultStartConditionSource = DEFAULT_SETTINGS.startConditionSource,
      comparator: defaultStartConditionComparator = DEFAULT_SETTINGS.startConditionComparator,
      level: defaultStartConditionLevel = DEFAULT_SETTINGS.startConditionLevel,
    }] = [{}],
    stopConditions: [{
      delay: defaultStopConditionDelay = DEFAULT_SETTINGS.stopConditionDelay,
      source: defaultStopConditionSource = DEFAULT_SETTINGS.stopConditionSource,
      comparator: defaultStopConditionComparator = DEFAULT_SETTINGS.stopConditionComparator,
      level: defaultStopConditionLevel = DEFAULT_SETTINGS.stopConditionLevel,
    }] = [{}],
  } = logicN

  const {
    type: newType,
    version: newVersion,
    relay: newRelay = actuator,
    action: newAction,
    runTimeMax: newRunTimeMax,
    runTimeMin: newRunTimeMin,
    runTimeCooldown: newRunTimeCooldown,
    startConditions: [{
      delay: newStartConditionDelay,
      source: newStartConditionSource,
      comparator: newStartConditionComparator,
      level: newStartConditionLevel,
    }] = [{}],
    stopConditions: [{
      delay: newStopConditionDelay,
      source: newStopConditionSource,
      comparator: newStopConditionComparator,
      level: newStopConditionLevel,
    }] = [{}],
  } = logicNewN

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [type, setType] = useState(newType || defaultType)
  const [version, setVersion] = useState(newVersion || defaultVersion)
  const [relay, setRelay] = useState(newRelay || defaultRelay)
  const [action, setAction] = useState(newAction || defaultAction)
  const [runTimeMax, setRunTimeMax] = useState(newRunTimeMax || defaultRunTimeMax)
  const [runTimeMin, setRunTimeMin] = useState(newRunTimeMin || defaultRunTimeMin)
  const [runTimeCooldown, setRunTimeCooldown] = useState(newRunTimeCooldown || defaultRunTimeCooldown)
  const [startConditionDelay, setStartConditionDelay] = useState(newStartConditionDelay || defaultStartConditionDelay)
  const [stopConditionDelay, setStopConditionDelay] = useState(newStopConditionDelay || defaultStopConditionDelay)
  const [startConditionSource, setStartConditionSource] = useState(newStartConditionSource || defaultStartConditionSource)
  const [stopConditionSource, setStopConditionSource] = useState(newStopConditionSource || defaultStopConditionSource)
  const [startConditionComparator, setStartConditionComparator] = useState(newStartConditionComparator || defaultStartConditionComparator)
  const [stopConditionComparator, setStopConditionComparator] = useState(newStopConditionComparator || defaultStopConditionComparator)
  const [startConditionLevel, setStartConditionLevel] = useState(newStartConditionLevel || defaultStartConditionLevel)
  const [stopConditionLevel, setStopConditionLevel] = useState(newStopConditionLevel || defaultStopConditionLevel)

  const appDispatch = useCallback(() => {
    const {
      device: {
        address,
        application_settings: {
          settings: {
            logic = {},
          } = {},
        } = {},
      },
    } = props

    logic[relay] = {
      type,
      version,
      relay: Number(relay),
      action,
      runTimeMax: Number(runTimeMax),
      runTimeMin: Number(runTimeMin),
      runTimeCooldown: Number(runTimeCooldown),
      startConditions: [
        {
          source: startConditionSource,
          comparator: startConditionComparator,
          level: Number(startConditionLevel),
          delay: Number(startConditionDelay),
        },
      ],
      stopConditions: [
        {
          source: stopConditionSource,
          comparator: stopConditionComparator,
          level: Number(stopConditionLevel),
          delay: Number(stopConditionDelay),
        },
      ],
    }

    const settings = {
      address,
      status: 'new',
      settings: {
        logic,
      },
    }
    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: settings,
    })
  }, [props, relay, type, version, action, runTimeMax, runTimeMin, runTimeCooldown, startConditionSource, startConditionComparator, startConditionLevel, startConditionDelay, stopConditionSource, stopConditionComparator, stopConditionLevel, stopConditionDelay])

  useEffect(() => {
    appDispatch()
  }, [relay, type, version, action, runTimeMax, runTimeMin, runTimeCooldown, startConditionSource, startConditionComparator, startConditionLevel, startConditionDelay, stopConditionSource, stopConditionComparator, stopConditionLevel, stopConditionDelay])

  const childChange = useCallback((event) => {
    const { target: { name, value } } = event
    let { target: { validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'action':
        if (ACTION_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setAction(newValue)
        break
      case 'startConditionSource':
      case 'stopConditionSource':
        if (SOURCE_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        if (name === 'startConditionSource') {
          setStartConditionSource(newValue)
        } else {
          setStopConditionSource(newValue)
        }
        break
      case 'startConditionComparator':
      case 'stopConditionComparator':
        if (COMPARATOR_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        if (name === 'startConditionComparator') {
          setStartConditionComparator(newValue)
        } else {
          setStopConditionComparator(newValue)
        }
        break
      case 'startConditionLevel':
      case 'stopConditionLevel':
        if (value !== '' && isNumber(value)) {
          valid = true
        } else {
          valid = false
        }
        if (name === 'startConditionLevel') {
          setStartConditionLevel(value)
        } else {
          setStopConditionLevel(value)
        }
        break
      case 'relay':
        setRelay(value)
        break
      case 'runTimeMax':
        setRunTimeMax(value)
        break
      case 'runTimeMin':
        setRunTimeMin(value)
        break
      case 'runTimeCooldown':
        setRunTimeCooldown(value)
        break
      case 'startConditionDelay':
        setStartConditionDelay(value)
        break
      case 'stopConditionDelay':
        setStopConditionDelay(value)
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity, appDispatch])

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.pump.logic.label'
            defaultMessage='Device Logic Settings'
            description='Device Logic Settings label'
          />
        </h3>
      </section>

      <div className={styles.inputArea}>
        <h4>
          <FormattedMessage
            id='setting.pump.logic.basics'
            defaultMessage='Basics'
            description='Basics label'
          />
        </h4>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.relay'
              defaultMessage='Relay'
              description='Relay label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={relay}
              options={RELAY_VALID}
              childChange={childChange}
              field='relay'
              style={styles.controlSelectInput}
              disabled
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.action'
              defaultMessage='Action'
              description='Action label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={action}
              options={ACTION_VALID}
              childChange={childChange}
              field='action'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <br />

        <h4>
          <FormattedMessage
            id='setting.pump.logic.startCondition'
            defaultMessage='Start Condition'
            description='Start Condition label'
          />
        </h4>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.source'
              defaultMessage='Source'
              description='Source label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={startConditionSource}
              options={SOURCE_VALID}
              childChange={childChange}
              field='startConditionSource'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.comparator'
              defaultMessage='Comparator'
              description='Comparator label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={startConditionComparator}
              options={COMPARATOR_VALID}
              childChange={childChange}
              field='startConditionComparator'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.level'
              defaultMessage='Level'
              description='Level label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={startConditionLevel}
                childChange={childChange}
                field='startConditionLevel'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.delay'
              defaultMessage='Time Delay'
              description='Time Delay label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={startConditionDelay}
              options={timeMaker(121, { value: 0, name: 'None' })}
              childChange={childChange}
              field='startConditionDelay'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <br />

        <h4>
          <FormattedMessage
            id='setting.pump.logic.stopCondition'
            defaultMessage='Stop Condition'
            description='Stop Condition label'
          />
        </h4>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.source'
              defaultMessage='Source'
              description='Source label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={stopConditionSource}
              options={SOURCE_VALID}
              childChange={childChange}
              field='stopConditionSource'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.comparator'
              defaultMessage='Comparator'
              description='Comparator label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={stopConditionComparator}
              options={COMPARATOR_VALID}
              childChange={childChange}
              field='stopConditionComparator'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.level'
              defaultMessage='Level'
              description='Level label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={stopConditionLevel}
                childChange={childChange}
                field='stopConditionLevel'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.delay'
              defaultMessage='Time Delay'
              description='Time Delay label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={stopConditionDelay}
              options={timeMaker(121, { value: 0, name: 'None' })}
              childChange={childChange}
              field='stopConditionDelay'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <br />

        <h4>
          <FormattedMessage
            id='setting.pump.logic.timeModifiers'
            defaultMessage='Time Modifiers'
            description='Time Modifiers label'
          />
        </h4>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.runTimeMax'
              defaultMessage='Maximum Run Time for Start'
              description='Maximum Run Time for Start label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={runTimeMax}
              options={timeMaker(121, { value: 0, name: 'Unlimited' })}
              childChange={childChange}
              field='runTimeMax'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.runTimeMin'
              defaultMessage='Minimum Run Time for Start'
              description='Minimum Run Time for Start label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={runTimeMin}
              options={timeMaker(121, false)}
              childChange={childChange}
              field='runTimeMin'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.logic.runTimeCooldown'
              defaultMessage='Time Between Actions'
              description='Time Between Actions label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={runTimeCooldown}
              options={timeMaker(121, false)}
              childChange={childChange}
              field='runTimeCooldown'
              style={styles.controlSelectInput}
            />
          </div>
        </section>

      </div>
    </div>
  )
}

Pump.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(Pump)
